import type { RouteLocationNormalized, Router } from "vue-router";
import { CurrentKycDtoStatus, type AuthProvider, type CustomerDto } from "@token/shared";
import { ExternalIdNotFoundError, customerFlowRouteName, type CustomerSession } from "@/features/customer-session";


export function installAuthGuard(router: Router, auth: AuthProvider, customerSession?: CustomerSession): void {
  let initialized = false;

  router.beforeEach(async (to) => {
    if (!initialized) {
      await auth.init();
      initialized = true;
    }

    if (to.meta.authCallback) {
      const redirect = await auth.handleCallback?.(new URL(window.location.href));
      if (auth.state.status === "authenticated") {
        return redirectToCustomerFlow(customerSession);
      }
      return redirect ?? { name: "home" };
    }

    const isAuthed = auth.state.status === "authenticated";

    if (to.meta.requiresAuth && !isAuthed) {
      if (auth.name === "auth0" || auth.providers?.includes("auth0")) {
        await auth.login("auth0", to.fullPath);
        return false;
      }

      return { name: "login", query: { redirect: to.fullPath } };
    }

    if (to.meta.guestOnly && isAuthed) {
      return redirectToCustomerFlow(customerSession);
    }

    if (to.meta.requiresAuth && isAuthed) {
      const customerResult = await syncCustomerForGuard(customerSession);
      if (customerResult === "external-id-not-found") {
        return to.name === "external-id-not-found" ? true : { name: "external-id-not-found" };
      }
      const customer = customerResult;
      const target = customer ? customerFlowRouteName(customer) : null;
      if (target && customer && !canStayOnCustomerRoute(to, target, customer)) {
        return { name: target };
      }
    }

    return true;
  });
}

async function redirectToCustomerFlow(customerSession?: CustomerSession) {
  const target = await customerFlowTarget(customerSession);
  return target ? { name: target } : { name: "home" };
}

async function customerFlowTarget(customerSession?: CustomerSession) {
  if (!customerSession) return null;
  const customer = await syncCustomerForGuard(customerSession);
  if (customer === "external-id-not-found") return "external-id-not-found";
  if (!customer) return null;
  return customerFlowRouteName(customer);
}

async function syncCustomerForGuard(customerSession?: CustomerSession): Promise<CustomerDto | "external-id-not-found" | null> {
  if (!customerSession) return null;
  try {
    return await customerSession.sync({ force: true });
  } catch (error) {
    if (error instanceof ExternalIdNotFoundError) {
      return "external-id-not-found";
    }
    throw error;
  }
}

function canStayOnCustomerRoute(
  to: RouteLocationNormalized,
  target: string,
  customer: CustomerDto,
): boolean {
  if (to.name === "home") return false;
  if (to.name === target) return true;
  if (to.name === "kyc-document-upload" && target === "kyc-status") return true;
  if (to.name === "kyc-status" && target === "token-purchase") return true;
  if (to.name === "payment-checkout" || to.name === "payment-return") return target === "token-purchase";
  return (
    target === "kyc-status" &&
    customer.kyc?.status === CurrentKycDtoStatus.REJECTED &&
    to.name === "onboarding" &&
    to.query.restart === "1"
  );
}
